/**
 * ChangeStore — the changes feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY changes module that talks to the API, and
 * it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<EngineeringChange>('changes')` + `api.identityProvider`). It
 * never imports ApiClient or calls fetch. React hooks call this store; pages
 * call the hook.
 *
 * Persistence model: each change is one document with id `chg_<uuid>` in the
 * real 'changes' generic-store collection (DDB pointer row + S3 body). The
 * feed-renderable fields (title, engineer, changed, why, impact, goalId,
 * workstreamId, related counts, createdAt, createdBy, deleted) are mirrored
 * onto the DDB pointer row's index attributes so the feed renders directly from
 * the pointer rows — WITHOUT hydrating S3 bodies per entry.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type {
  EngineeringChange,
  EngineeringChangeDraft,
  EngineeringChangeSummary,
} from './types';
import { changeDocId } from './changeModel';

export const CHANGES_COLLECTION = 'changes';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  title: 'title',
  engineer: 'engineer',
  changed: 'changed',
  why: 'why',
  impact: 'impact',
  goalId: 'goalId',
  workstreamId: 'workstreamId',
  relatedStoryCount: 'relatedStoryCount',
  relatedDocCount: 'relatedDocCount',
  createdAt: 'createdAt',
  createdBy: 'createdBy',
  deleted: 'deleted',
} as const;

export class ChangeStore {
  private readonly docs: DocumentStore<EngineeringChange>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<EngineeringChange>(CHANGES_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(change: EngineeringChange): StringMap {
    return {
      [ROW.title]: change.title ?? '',
      [ROW.engineer]: change.engineer ?? '',
      [ROW.changed]: change.changed ?? '',
      [ROW.why]: change.why ?? '',
      [ROW.impact]: change.impact ?? '',
      [ROW.goalId]: change.goalId ?? '',
      [ROW.workstreamId]: change.workstreamId ?? '',
      [ROW.relatedStoryCount]: String((change.relatedStoryIds ?? []).length),
      [ROW.relatedDocCount]: String((change.relatedDocIds ?? []).length),
      [ROW.createdAt]: change.createdAt ?? '',
      [ROW.createdBy]: change.createdBy ?? '',
      [ROW.deleted]: change.deleted ? 'true' : 'false',
    };
  }

  /** Projects a raw pointer row into a lightweight summary. */
  private toSummary(row: StringMap): EngineeringChangeSummary {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      title: row[ROW.title] ?? '',
      engineer: row[ROW.engineer] ?? '',
      changed: row[ROW.changed] ?? '',
      why: row[ROW.why] ?? '',
      impact: row[ROW.impact] ?? '',
      goalId: row[ROW.goalId] || undefined,
      workstreamId: row[ROW.workstreamId] || undefined,
      relatedStoryCount: Number(row[ROW.relatedStoryCount] ?? '0') || 0,
      relatedDocCount: Number(row[ROW.relatedDocCount] ?? '0') || 0,
      createdAt: row[ROW.createdAt] ?? '',
      createdBy: row[ROW.createdBy] ?? '',
    };
  }

  /**
   * Lists all non-deleted change summaries. Filters to this collection
   * defensively (the underlying list is not collection-scoped).
   */
  async listSummaries(): Promise<EngineeringChangeSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === CHANGES_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single change document by id (hydrates the S3 body). */
  async get(id: string): Promise<EngineeringChange | undefined> {
    return this.docs.get(id);
  }

  /**
   * Creates a new change. Seeds id, createdBy (from identity) and createdAt,
   * and returns the saved document.
   */
  async create(draft: EngineeringChangeDraft): Promise<EngineeringChange> {
    const identity = await this.api.identityProvider.getIdentity();
    const change = this.fromDraft(changeDocId(), draft, identity.alias, undefined);
    await this.docs.save(change.id, change, this.indexAttributes(change));
    return change;
  }

  /**
   * Applies a draft's fields to an existing change in place (same id/createdBy/
   * createdAt) and re-mirrors index attributes.
   */
  async applyDraft(
    change: EngineeringChange,
    draft: EngineeringChangeDraft,
  ): Promise<EngineeringChange> {
    const updated = this.fromDraft(
      change.id,
      draft,
      change.createdBy,
      change.createdAt,
    );
    return this.update(updated);
  }

  /** Persists a full change document in place. */
  async update(change: EngineeringChange): Promise<EngineeringChange> {
    const updated: EngineeringChange = { ...change, deleted: false };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Soft-deletes a change: fetches it, flags it deleted, and updates both the
   * S3 body and the pointer row so queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: EngineeringChange = { ...existing, deleted: true };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }

  /**
   * Builds a full EngineeringChange from a draft, seeding id/createdBy and
   * preserving createdAt on edit (new entries stamp "now").
   */
  private fromDraft(
    id: string,
    draft: EngineeringChangeDraft,
    createdBy: string,
    existingCreatedAt: string | undefined,
  ): EngineeringChange {
    return {
      id,
      title: draft.title.trim(),
      engineer: draft.engineer.trim(),
      changed: draft.changed ?? '',
      why: draft.why ?? '',
      impact: draft.impact ?? '',
      relatedDocIds: draft.relatedDocIds ?? [],
      relatedStoryIds: draft.relatedStoryIds ?? [],
      goalId: draft.goalId || undefined,
      workstreamId: draft.workstreamId || undefined,
      createdAt: existingCreatedAt ?? new Date().toISOString(),
      createdBy,
      deleted: false,
    };
  }
}
